"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Manifesto() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const imageWrapRef = useRef<HTMLDivElement | null>(null);
  const [parallaxY, setParallaxY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const node = imageWrapRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const progress = Math.max(0, Math.min(1, 1 - rect.top / windowHeight));
      setParallaxY(progress * 30);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const benefits = ["Limited production runs for exclusivity", "Museum-grade archival film prints", "Direct acquisition from the artist"];
  const stats = [{
    value: "1/1",
    label: "EDITION"
  }, {
    value: "48 HR",
    label: "SHIPPING"
  }, {
    value: "100%",
    label: "PROVENANCE"
  }];
  return <section data-section-id="564" id="the-film-manifesto" className="relative w-full min-h-[80vh] bg-background text-foreground overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
        {}
        <div ref={imageWrapRef} className="relative w-full h-[50vh] lg:h-auto overflow-hidden">
          <div className="absolute inset-0 will-change-transform" style={{
          transform: `translate3d(0, ${parallaxY}px, 0)`
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/john_john_gallery_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/john_john_gallery_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713483/img/john_john_gallery_hero.png-wvc-sizes" alt="Ultra high end film photography gallery" loading="lazy" decoding="async" className="w-full h-full object-cover scale-110" />
            {}
            <div className="absolute inset-0 pointer-events-none" style={{
            background: "linear-gradient(115deg, hsla(0, 0%, 0%, 0.8) 0%, hsla(0, 0%, 0%, 0.2) 45%, hsla(0, 0%, 100%, 0.05) 100%)"
          }} />
          </div>
          {}
          <div className="hidden lg:block absolute top-0 right-0 h-full w-24 pointer-events-none" style={{
          background: "linear-gradient(90deg, hsla(0, 0%, 0%, 0) 0%, hsl(0, 0%, 0%) 100%)"
        }} />
        </div>

        {}
        <div className="relative flex items-center bg-background">
          <div ref={contentRef} className={`w-full px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-2xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-foreground/70">
              <span className="inline-block w-1.5 h-1.5 rounded-lg bg-foreground" style={{
              boxShadow: "0 0 12px hsla(0, 0%, 100%, 0.7)"
            }} aria-hidden="true" />
              <span>Manifesto</span>
            </div>

            {}
            <h1 className="font-default font-light leading-[1.05] mt-6 text-foreground" style={{
            fontSize: "clamp(2.5rem, 6vw, 5rem)"
          }}>
              The captured moment.{" "}
              <em className="not-italic">
                <span className="italic font-normal text-foreground">
                  Refined for eternity.
                </span>
              </em>
            </h1>

            {}
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground/75 font-default max-w-xl">
              John John Fine Photography represents the pinnacle of limited production film artistry. We curate deliberate, high-end visual narratives designed for the discerning collector, ensuring every frame remains a singular, museum-grade experience.
            </p>

            {}
            <ul className="mt-10 space-y-4">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className={`flex items-start gap-4 transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"}`} style={{
              transitionDelay: `${150 + i * 100}ms`
            }}>
                  <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-lg border border-foreground/40 bg-foreground/10">
                    <Check className="w-3 h-3 text-foreground" strokeWidth={3} />
                  </span>
                  <span className="text-foreground/90 text-base sm:text-lg font-default">
                    {benefit}
                  </span>
                </li>)}
            </ul>

            {}
            <div className="mt-12 pt-8 border-t border-foreground/15">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-4 font-mono text-xs sm:text-sm tracking-[0.15em] uppercase">
                {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-x-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-foreground font-semibold">
                        {stat.value}
                      </span>
                      <span className="text-foreground/55">
                        {stat.label}
                      </span>
                    </div>
                    {i < stats.length - 1 && <span className="inline-block w-1 h-1 rounded-lg bg-foreground" aria-hidden="true" />}
                  </div>)}
              </div>
            </div>

            {}
            <div className="mt-10">
              <Button asChild size="lg" className="group h-12 px-7 font-mono text-xs sm:text-sm tracking-[0.2em] uppercase bg-foreground text-background hover:bg-foreground/90 rounded-lg">
                <Link to="/ShopPage">
                  Acquire Collection
                  <ArrowDown className="ml-1 w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}